<template lang="pug">
div.neutral200(:class="isMobileScreen ? 'pt-4 px-4 pb-4' : 'pa-8'" style="min-height: calc(100dvh - 72px);")
  //- Header section
  .d-flex.align-center(:class="isMobileScreen ? 'justify-end' : 'justify-space-between'")
    .yz-heading-5.text-neutral-700(v-if="!isMobileScreen") Stock take
    yz-dropdown(v-model="selectedWarehouseSlug" :items="warehouses" item-text="name" item-value="slug" placeholder="Select warehouse" :loading="isLoadingWarehouses" size="lg" :style="isMobileScreen ? 'width: 100%;' : 'min-width: 220px;'")

  yz-card.mt-4(outlined)
    .px-4.pt-2
      //- Desktop: Tabs and actions row
      template(v-if="!isMobileScreen")
        v-row(no-gutters align="center")
          v-col
            yz-tabs(v-model="tabs" type="line" @change="onTabChanged")
              yz-tab(:label="$t('all')")
              yz-tab(label="Archived")
          v-col(align="end")
            .d-flex.align-center.justify-end
              yz-search-btn(v-model="searchText" @search="onSearch")
              div.ml-1
                customize-column-menu(type="hq_stocktake" :showTooltip="false" iconSize="21")
              yz-btn.ml-3(label="Start stock take" size="lg" @click="onStartStockTake")

      //- Mobile: Compact actions and scrollable tabs
      template(v-else)
        .d-flex.align-center.justify-space-between.mb-2
          yz-search-btn(v-model="searchText" @search="onSearch")
          yz-btn(icon-only icon="$yzi-plus" size="lg" @click="onStartStockTake")
        .overflow-x-auto
          .d-flex.align-center(style="width: max-content;")
            div(v-for="tab in tabItems" :key="tab.id")
              .px-3.py-2.cursor-pointer(@click="tabs = tab.id; onTabChanged()")
                span.yz-medium-body-sm(:class="tabs === tab.id ? 'neutral700--text' : 'neutral500--text'") {{ tab.label }}
              .bar-active(v-if="tabs === tab.id")

    yz-divider

    filter-stocktakes-row.mt-4.px-4(ref="filter" :filters.sync="filters")

    .d-flex.align-center.mt-4.px-4(v-if="!isMobileScreen && checkedStocktakes.length")
      yz-btn.mr-3(label="Archive" size="lg" color="danger" outlined @click="openMultiArchiveDialog = true")
      yz-btn.mr-3(label="Download report PDF" size="lg" @click="onBulkDownload")
      a.yz-regular-body-sm.text-neutral-500(@click="onClearSelection") {{ $t('clear_selection') }}

    //- Mobile card list view
    template(v-if="isMobileScreen")
      .px-4.mt-4
        //- Loading skeleton
        template(v-if="isFetching")
          v-card.rounded-lg.mb-3(v-for="n in 3" :key="'skeleton-' + n" flat outlined)
            .pa-4
              v-skeleton-loader(type="list-item-three-line")

        //- Empty state
        .text-center.py-8(v-else-if="!stocktakes.length")
          .yz-regular-body-sm.neutral500--text No items found

        //- Card list
        template(v-else)
          v-card.rounded-lg.mb-3(v-for="item in stocktakes" :key="item.id" flat outlined @click="onRowClick(item)")
            .pa-4
              //- Header: Name + Status badge + 3-dot menu
              .d-flex.align-center.justify-space-between
                .d-flex.align-center.gap-1.flex-grow-1(style="min-width: 0;")
                  .yz-medium-body-sm.neutral700--text.text-truncate {{ item.name }}
                  yz-badge(v-if="item.archived" x-small color="danger-100" text-color="danger-500") Archived
                  yz-badge(v-else-if="item.status === 'in_progress'" x-small color="warning-100" text-color="warning-500") In progress
                  yz-badge(v-else x-small color="success-100" text-color="success-500") Finished
                v-menu(offset-y left rounded="lg")
                  template(#activator="{ on }")
                    yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="sm" v-on="on" @click.native.stop)
                  v-list.py-0
                    template(v-if="item.status === 'finished'")
                      v-list-item(@click="onViewDetails(item)")
                        v-list-item-title.yz-regular-body-sm.text-neutral-700 View details
                      v-divider
                    template(v-if="item.status === 'finished'")
                      v-list-item(@click="onDownloadReport(item)")
                        v-list-item-title.yz-regular-body-sm.text-neutral-700 Download PDF
                      v-divider
                    v-list-item(@click="onArchiveAction(item)")
                      v-list-item-title.yz-regular-body-sm(:class="!item.archived ? 'text-danger-500' : 'text-success-500'") {{ !item.archived ? 'Archive' : 'Activate' }}

              //- User
              .d-flex.align-center.mt-3(v-if="item.member")
                .yz-regular-body-xs.neutral500--text.mr-2 User
                .yz-regular-body-xs.neutral700--text {{ item.member.name }}

              //- Start date
              .d-flex.align-center.mt-2(v-if="item.created")
                .yz-regular-body-xs.neutral500--text.mr-2 Start date
                .yz-regular-body-xs.neutral700--text {{ item.created | humanizeDate }} {{ item.created | displayTime2 }}

              //- End date
              .d-flex.align-center.mt-2(v-if="item.finished_at")
                .yz-regular-body-xs.neutral500--text.mr-2 End date
                .yz-regular-body-xs.neutral700--text {{ item.finished_at | humanizeDate }} {{ item.finished_at | displayTime2 }}

              //- Item count
              .d-flex.align-center.mt-2(v-if="item.products && item.products.length")
                .yz-regular-body-xs.neutral500--text.mr-2 Items
                .yz-regular-body-xs.neutral700--text {{ item.products.length }}

    //- Desktop table view
    .table-scroll-wrapper(v-else :class="{ 'has-data': stocktakes.length > 0 }")
      yz-data-table.mt-2(:headers="headers" :items="stocktakes" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" no-outer-border radius="0" body-height="52px" header-height="36px" header-class="yz-bold-body-xs" header-background-color="white" show-select disable-sort @click:row="onRowClick")
        template(v-slot:header.data-table-select)
          yz-checkbox(:value="allSelected" :indeterminate="someSelected" @change="onToggleSelectAll")
        template(v-slot:item.data-table-select="{ item }")
          yz-checkbox(:value="isSelected(item.id)" @change="onToggleSelect(item)" @click.native.stop)
        template(v-slot:item.name="{ item }")
          .yz-regular-body-sm.text-neutral-700 {{ item.name }}
        template(v-slot:item.user="{ item }")
          .yz-regular-body-sm.text-neutral-700 {{ item.member ? item.member.name : '-' }}
        template(v-slot:item.inventory="{ item }")
          .yz-regular-body-sm.text-neutral-700 {{ item.products && item.products.length ? item.products.length : '-' }}
        template(v-slot:item.start_date="{ item }")
          div(v-if="item.created")
            .yz-regular-body-sm.text-neutral-700 {{ item.created | humanizeDate }}
            .yz-regular-body-xs.text-neutral-500 {{ item.created | displayTime2 }}
          .yz-regular-body-sm.text-neutral-700(v-else) -
        template(v-slot:item.end_date="{ item }")
          div(v-if="item.finished_at")
            .yz-regular-body-sm.text-neutral-700 {{ item.finished_at | humanizeDate }}
            .yz-regular-body-xs.text-neutral-500 {{ item.finished_at | displayTime2 }}
          .yz-regular-body-sm.text-neutral-700(v-else) -
        template(v-slot:item.status="{ item }")
          yz-badge(v-if="item.archived" color="danger-100" text-color="danger-500") Archived
          yz-badge(v-else-if="item.status === 'in_progress'" color="warning-100" text-color="warning-500") In progress
          yz-badge(v-else color="success-100" text-color="success-500") Finished
        template(v-slot:item.actions="{ item, index }")
          div.d-flex.align-center.justify-end(@click.stop)
            yz-btn(v-if="item.status === 'finished'" label="PDF" size="lg" text icon="$yzi-download-01" @click="onDownloadReport(item)")
            yz-btn(v-else label="Continue" size="lg" text @click="onViewDetails(item)")
            yz-menu(offset-y :top="menuPositionTop(index)" left)
              template(#activator="{ on }")
                yz-btn(v-on="on" icon-only icon="mdi-dots-horizontal" size="lg" color="transparent")
              v-list.py-0
                template(v-if="item.status === 'finished'")
                  v-list-item(@click="onViewDetails(item)")
                    v-list-item-title.yz-regular-body-sm.text-neutral-700 View details
                  v-divider
                v-list-item(@click="onArchiveAction(item)")
                  v-list-item-title.yz-regular-body-sm(:class="!item.archived ? 'text-danger-500' : 'text-success-500'") {{ !item.archived ? 'Archive' : 'Activate' }}

    yz-pagination.mt-4.px-4(v-if="totalCount > pageSize" v-model="currentPage" :page-size.sync="pageSize" :count="totalCount" change-page-size arrow @input="onPageChanged")

  .dialogs
    new-stock-take-dialog(v-if="openStockTakeDialog" :open.sync="openStockTakeDialog" :warehouse-slug="selectedWarehouseSlug" @created="onStockTakeCreated")
    yz-delete-confirmation-dialog(v-model="openArchiveDialog" item="stock take" description="This item will be transferred into the \"Archived\" tab. Select \"Confirm\" to proceed." :loading="isLoading" @confirmed="onConfirmArchive" @canceled="onClosedArchiveDialog")
    yz-delete-confirmation-dialog(v-model="openMultiArchiveDialog" item="selected stock takes" description="These items will be transferred into the \"Archived\" tab. Select \"Confirm\" to proceed." :loading="isLoading" @confirmed="onConfirmArchive(true)" @canceled="onClosedArchiveDialog")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import NewStockTakeDialog from '@/components/clinic/inventory/stocktake/NewStockTakeDialog.vue'
import FilterStocktakesRow from '@/components/clinic/inventory/FilterStocktakesRow.vue'

export default {
  name: 'InventoryStockTake',
  components: { CustomizeColumnMenu, NewStockTakeDialog, FilterStocktakesRow },
  data: () => ({
    isFetching: false,
    isLoading: false,
    isLoadingWarehouses: false,
    searchText: '',
    pageSize: 20,
    totalCount: 0,
    currentPage: 1,
    stocktakes: [],
    tabs: 0,
    filters: [],
    warehouses: [],
    selectedWarehouseSlug: null,
    checkedStocktakes: [],
    openStockTakeDialog: false,
    openArchiveDialog: false,
    openMultiArchiveDialog: false,
    selectedStocktake: null,
  }),
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany',
      stocktakeFields: 'hq/getStocktakeFields'
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    tabItems () {
      return [
        { id: 0, label: 'All' },
        { id: 1, label: 'Archived' }
      ]
    },
    hqCompanyId () {
      return this.company?.branch_of?.id || this.company?.id
    },
    headers () {
      const name = { text: 'Name', value: 'name', enable: true }
      const fields = this.stocktakeFields.filter(item => item.enable)
      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
      })
      const actions = { text: '', value: 'actions', enable: true, sortable: false }
      return [name, ...fields, actions]
    },
    allSelected () {
      return this.stocktakes.length > 0 && this.stocktakes.every(item => this.checkedStocktakes.find(s => s.id === item.id))
    },
    someSelected () {
      const hasSelected = this.stocktakes.some(item => this.checkedStocktakes.find(s => s.id === item.id))
      return hasSelected && !this.allSelected
    },
  },
  watch: {
    filters () {
      this.currentPage = 1
      this.fetchStockTakes()
    },
    selectedWarehouseSlug () {
      this.currentPage = 1
      this.fetchStockTakes()
    },
  },
  mounted () {
    this.fetchWarehouses()
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchWarehouses () {
      this.isLoadingWarehouses = true
      try {
        const resp = await this.$axios.get(`/companies/${this.hqCompanyId}/branches`, { params: { os: 'warehouse', page_size: -1 } })
        this.warehouses = resp.data?.results || []
        if (this.warehouses.length > 0 && !this.selectedWarehouseSlug) {
          const querySlug = this.$route.query.warehouseSlug
          if (querySlug && this.warehouses.find(w => w.slug === querySlug)) {
            this.selectedWarehouseSlug = querySlug
          } else {
            this.selectedWarehouseSlug = this.warehouses[0].slug
          }
        }
      } catch {
        this.warehouses = []
      } finally {
        this.isLoadingWarehouses = false
      }
    },
    fetchStockTakes () {
      if (!this.selectedWarehouseSlug) return
      this.isFetching = true
      const params = {
        page_size: this.pageSize,
        page: this.currentPage
      }
      if (this.tabs === 0) { params.archived = false }
      if (this.tabs === 1) { params.archived = true }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      if (this.searchText) {
        params.search = this.searchText
      }
      this.$axios.get(`/clinics/${this.selectedWarehouseSlug}/stock_takes`, { params })
        .then(resp => {
          this.stocktakes = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(err => {
          const message = err.response?.data?.detail
          if (message && message.includes('Invalid page')) {
            this.currentPage = 1
            this.fetchStockTakes()
          }
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    onSearch () {
      this.currentPage = 1
      this.fetchStockTakes()
    },
    onPageChanged () {
      this.fetchStockTakes()
    },
    onTabChanged () {
      this.onClearSelection()
      this.searchText = ''
      this.currentPage = 1
      this.fetchStockTakes()
    },
    onRowClick (data) {
      this.onViewDetails(data)
    },
    isSelected (id) {
      return this.checkedStocktakes.some(item => item.id === id)
    },
    onToggleSelect (item) {
      if (this.isSelected(item.id)) {
        this.checkedStocktakes = this.checkedStocktakes.filter(i => i.id !== item.id)
      } else {
        this.checkedStocktakes.push(item)
      }
    },
    onToggleSelectAll (value) {
      if (value) {
        this.checkedStocktakes = [...this.stocktakes]
      } else {
        this.checkedStocktakes = []
      }
    },
    onClearSelection () {
      this.checkedStocktakes = []
    },
    onStartStockTake () {
      this.openStockTakeDialog = true
    },
    onStockTakeCreated (stockTake) {
      if (stockTake && stockTake.id) {
        this.$router.push({ name: 'HQStockTakeDetail', params: { id: stockTake.id }, query: { warehouseSlug: this.selectedWarehouseSlug } })
      } else {
        this.fetchStockTakes()
      }
    },
    onViewDetails (item) {
      if (item.status === 'finished') {
        this.$router.push({ name: 'HQStockTakeSummary', params: { id: item.id }, query: { warehouseSlug: this.selectedWarehouseSlug } })
      } else {
        this.$router.push({ name: 'HQStockTakeDetail', params: { id: item.id }, query: { warehouseSlug: this.selectedWarehouseSlug } })
      }
    },
    onArchiveAction (item) {
      if (item.archived) {
        this.isFetching = true
        this.$axios.patch(`/clinics/${this.selectedWarehouseSlug}/stock_takes/${item.id}`, { archived: false })
          .then(() => {
            this.fetchStockTakes()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isFetching = false
          })
      } else {
        this.selectedStocktake = item
        this.openArchiveDialog = true
      }
    },
    async onConfirmArchive (isMultiple = false) {
      this.isLoading = true
      if (isMultiple) {
        try {
          for (let i = 0; i < this.checkedStocktakes.length; i++) {
            await this.$axios.patch(`/clinics/${this.selectedWarehouseSlug}/stock_takes/${this.checkedStocktakes[i].id}`, { archived: true })
          }
          this.onClearSelection()
          this.fetchStockTakes()
        } catch {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        } finally {
          this.isLoading = false
          this.onClosedArchiveDialog()
        }
      } else {
        this.$axios.patch(`/clinics/${this.selectedWarehouseSlug}/stock_takes/${this.selectedStocktake.id}`, { archived: true })
          .then(() => {
            this.fetchStockTakes()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
            this.onClosedArchiveDialog()
          })
      }
    },
    async onDownloadReport (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      if (item.status === 'finished') {
        const url = `${baseUrl}/clinics/${this.selectedWarehouseSlug}/stock_takes/${item.id}/preview_report?key=${key}`
        window.open(url, '_blank')
      }
    },
    async onBulkDownload () {
      for (const stocktake of this.checkedStocktakes) {
        await this.onDownloadReport(stocktake)
      }
    },
    onClosedArchiveDialog () {
      this.selectedStocktake = null
      this.openArchiveDialog = false
      this.openMultiArchiveDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 7) {
        let start = this.stocktakes.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },
  }
}
</script>

<style lang="scss" scoped>
.has-data ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
.overflow-x-auto {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
  scrollbar-width: none;
}
.bar-active {
  height: 2px;
  width: 100%;
  margin: auto;
  margin-top: -2px;
  background-color: #333;
}
</style>
